import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, switchMap, of, throwError, catchError } from 'rxjs';
import { environment } from '../environments/environment';
import { CalAngularService } from '@cvx/cal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { APP_ERROR_CODES, getUserMessage } from '../app/services/app-error-catalog';
import { createAppError } from '../app/services/app-error.model';
import { LoggerService } from '../app/services/logger.service';
import { isAppError } from '../app/services/app-error.model';

@Injectable({
  providedIn: 'root'
})

/**
 * Central HTTP gateway for BC/FO backend endpoints.
 *
 * Conventions used in this service:
 * - Keep legacy method names to preserve compatibility with existing components.
 * - Route every call through makeAuthenticatedRequest so AAD token retrieval is consistent.
 * - Reuse content-type specific configs (plainText/json/csv) to match backend expectations.
 */
export class AllServicesService {

  private readonly url = (environment.hostApiURL || '').replace(/\/+$/, '');
  private readonly apiScope =
    environment.apiScope || '808e64c3-7d7a-4ca4-9a43-d71765221e17/user_impersonation';

  // HTTP configurations with appropriate headers
  private readonly plainTextConfig = {
    headers: new HttpHeaders({
      'Content-Type': 'text/plain;charset=utf-8',
    }),
    withCredentials: true
  };

  private readonly jsonConfig = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
    withCredentials: true
  };

  private readonly csvConfig = {
    headers: new HttpHeaders({
      'Content-Type': 'text/csv',
    }),
    withCredentials: true
  };

  constructor(
    private http: HttpClient,
    private authService: CalAngularService,
    private logger: LoggerService
  ) {
  }

  // #region Authentication and request wrappers

   /**
   * Helper method to get headers with a valid authentication token
    * Uses exclusively the CAL flow for authentication.
    *
    * The `source` parameter is propagated into AppError so auth/session failures
    * can be traced back to the originating endpoint wrapper.
   */
  private getAuthHeaders(source: string): Observable<HttpHeaders> {
    return this.authService.isUserSignedIn().pipe(
      switchMap((isSignedIn) => {
        if (!isSignedIn) {
          return throwError(() => createAppError({
            category: 'design',
            code: APP_ERROR_CODES.AUTH_REQUIRED,
            source,
            userMessage: getUserMessage(APP_ERROR_CODES.AUTH_REQUIRED),
            technicalMessage: 'User not authenticated',
            displayMode: 'snackbar'
          }));
        }
        return this.authService.getAADToken([this.apiScope]);
      }),
      switchMap((token: string | AuthenticationResult | null) => {
        const accessToken =
          typeof token === 'string'
            ? token
            : token && typeof token === 'object' && 'accessToken' in token
              ? (token as AuthenticationResult).accessToken
              : null;

        if (!accessToken) {
          const error = createAppError({
            category: 'design',
            code: APP_ERROR_CODES.AUTH_TOKEN_MISSING,
            source,
            userMessage: getUserMessage(APP_ERROR_CODES.AUTH_TOKEN_MISSING),
            technicalMessage: 'Could not extract access token',
            displayMode: 'snackbar'
          });
          this.logger.error(error);
          return throwError(() => error);
        }

        return of(new HttpHeaders().set('Authorization', `Bearer ${accessToken}`));
      }),
      catchError((error) => {
        if (isAppError(error)) {
          return throwError(() => error);
        }

        const appError = createAppError({
          category: 'design',
          code: APP_ERROR_CODES.AUTH_REQUIRED,
          source,
          userMessage: getUserMessage(APP_ERROR_CODES.AUTH_REQUIRED),
          technicalMessage: 'Authentication recovery failed',
          originalError: error,
          displayMode: 'snackbar'
        });
        this.logger.error(appError);
        return throwError(() => appError);
      })
    );
  }

  /**
   * Generic method to make authenticated requests
   * Waits to obtain the token before executing the request.
   *
   * This is the service-boundary normalization point for endpoint context: every
   * wrapper passes its own source identifier so upstream error handling has enough
   * information to classify and log failures consistently.
   */
  private makeAuthenticatedRequest<T>(
    source: string,
    requestFn: (headers: HttpHeaders) => Observable<T>
  ): Observable<T> {
    return this.getAuthHeaders(source).pipe(
      switchMap((headers) => requestFn(headers))
    );
  }

  // #endregion

  // #region Blend calculator and model endpoint wrappers

  getdropdown(): Observable<any> {

    return this.makeAuthenticatedRequest('getdropdown', (headers) =>
      this.http.get(`${this.url}/api/getdata/`, { headers })
    );
  }


  // In feature_importance_data we are getting the data to show the data in frontend in footer table.
  // In feature selection column we have 8 buttons and we can see different data based on button selection
  feature_importance_data(): Observable<any> {

    return this.makeAuthenticatedRequest('feature_importance_data', (headers) =>
      this.http.get(`${this.url}/api/get_feture_importance_data/`, { headers })
    );
  }

  // In get_vm_data we are getting the values to populate in kv100CSt column after selecting vm dropdown.
  get_vm_data(packages: any): Observable<any> {

    return this.makeAuthenticatedRequest('get_vm_data', (headers) =>
      this.http.post(`${this.url}/api/getVMdata/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // In get_ppd_data we are getting the values to populate in kv100CSt column after selecting ppd dropdown.
  get_ppd_data(packages: any): Observable<any> {

    return this.makeAuthenticatedRequest('get_ppd_data', (headers) =>
      this.http.post(`${this.url}/api/getPPDdata/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // In get_Batch_data we are getting the values to populate in kv100CSt column and vi column after selecting Base oil dropdown.
  get_Batch_data(packages: any): Observable<any> {

    return this.makeAuthenticatedRequest('get_Batch_data', (headers) =>
      this.http.post(`${this.url}/api/getBatchdata/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // In get_DI_data we are getting the values to populate in package row treat wt % column for prediction 1 and prediction 2 in finished oil table and also data is populating in wt% , dil% ,data for package table
  get_DI_data(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_DI_data', (headers) =>
      this.http.post(`${this.url}/api/getDIdata/`, { data: packages }, { ...this.jsonConfig, headers })
    );
  }

  //  In get_BO_wt_data we are getting the values to populate in treat wt% column for prediction 1 in finished oil table when sum = 100.
  get_BO_wt_data(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_BO_wt_data', (headers) =>
      this.http.post(`${this.url}/api/getBOwtdata/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // In get_BO_wt_data we are getting the values to populate in treat wt% column for prediction 2 in finished oil table when sum = 100.
  get_BO_wt_data_pred_2(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_BO_wt_data_pred_2', (headers) =>
      this.http.post(`${this.url}/api/getBOwtdataPred2/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // In get_di_dosage_adjust we sending the values of package table data and changed value in treat wt % in finished oil table to backend, and we are getting new package table data. this will trigger when we change the value in package row in finished oil table.
  get_di_dosage_adjust(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_di_dosage_adjust', (headers) =>
      this.http.post(`${this.url}/api/getdiDosageAdjust/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  //  when we change the package dil % or values in Treat in Finshed Oil column in  package table, we are updating the 'Blend Metal Typical' column and 'Wt% in Dl Package' column in package table
  get_di_wt_update(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_di_wt_update', (headers) =>
      this.http.post(`${this.url}/api/getDTwtupdate/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  //  when we change the dropdown in 'component name' column in package table,we are updating the 'Treat in Finshed Oil' column in package table
  //  and get_di_wt_update function updating the 'Blend Metal Typical' column and 'Wt% in Dl Package' column in package table
  get_DI_update(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_DI_update', (headers) =>
      this.http.post(`${this.url}/api/get_DI_update/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  //In get_predictions we are getting predictions and true/false from backend to populate the data in results table
  get_predictions(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_predictions', (headers) =>
      this.http.post(`${this.url}/api/get_predictions/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // In get_model_inputs we are getting model inputs 1 data when model input 1 button color changes to red
  get_Model_Inputs(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_Model_Inputs', (headers) =>
      this.http.post(`${this.url}/api/get_Model_Inputs/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // In get_model_inputspred2 we are getting model inputs 2 data when model input 2 button color changes to red
  get_Model_InputsPred2(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_Model_InputsPred2', (headers) =>
      this.http.post(`${this.url}/api/get_Model_Inputs_pred_2/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // In get_prediction_Model_Inputs we are getting prediction values and true or false when we click on submit button in model input 1 or 2 buttons
  get_Prediction_Model_Inputs(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_Prediction_Model_Inputs', (headers) =>
      this.http.post(`${this.url}/api/get_predictions_model_inputs/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  ClearPrediction1(): Observable<any> {
    return this.makeAuthenticatedRequest('ClearPrediction1', (headers) =>
      this.http.get(`${this.url}/api/clear_prediciton_1/`, { headers })
    );
  }

  ClearPrediction2(): Observable<any> {
    return this.makeAuthenticatedRequest('ClearPrediction2', (headers) =>
      this.http.get(`${this.url}/api/clear_prediciton_2/`, { headers })
    );
  }

  // get_export_data we are sending the package table data to backend, after changing the dropdown in component column, to show different dropdowns between pred1 and pred 2 in excel we getting the merge data from backend
  get_export_data(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_export_data', (headers) =>
      this.http.post(`${this.url}/api/getexportdata/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // we are getting the user details when user loggedin into the app
  userloggedin(): Observable<any> {
    return this.makeAuthenticatedRequest('userloggedin', (headers) =>
      this.http.get(`${this.url}/.auth/me`, { headers })
    );
  }

  // After getting all mails from distribution list we are storing all mails in blob
  access_mails_to_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('access_mails_to_blob', (headers) =>
      this.http.post(`${this.url}/api/access_mails_to_blob/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  //  here we are getting all distribution lists from blob to show in access mails
  get_dist_list_from_blob(): Observable<any> {
    return this.makeAuthenticatedRequest('get_dist_list_from_blob', (headers) =>
      this.http.get(`${this.url}/api/get_dist_list_from_blob/`, { headers })
    );
  }

  //  After adding new distribution list in access mail we are storing that mail in blob
  get_add_dist_list_to_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_add_dist_list_to_blob', (headers) =>
      this.http.post(`${this.url}/api/get_add_dist_list_to_blob/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  //  After deleting any distribution list in access mail we are storing remaining mails in blob
  get_delete_dist_list_to_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_delete_dist_list_to_blob', (headers) =>
      this.http.post(`${this.url}/api/get_delete_dist_list_to_blob/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  //  here we are getting all admins mails from blob to show in access mails
  get_admin_list_from_blob(): Observable<any> {
    return this.makeAuthenticatedRequest('get_admin_list_from_blob', (headers) =>
      this.http.get(`${this.url}/api/get_admin_list_from_blob/`, { headers })
    );
  }

  //  After adding new admin in access mail we are storing remaining mails in blob
  get_add_admin_list_to_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_add_admin_list_to_blob', (headers) =>
      this.http.post(`${this.url}/api/get_add_admin_list_to_blob/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  //  After deleting any admin mail in access mail we are storing remaining mails in blob
  get_delete_admin_list_to_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_delete_admin_list_to_blob', (headers) =>
      this.http.post(`${this.url}/api/get_delete_admin_list_to_blob/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  //In get_dist_list_mails we getting distribution mails ID from Microsoft graph API and from ID we are getting mails
  get_dist_list_mails(): Observable<any> {
    return this.makeAuthenticatedRequest('get_dist_list_mails', (headers) =>
      this.http.get(`${this.url}/.auth/me`, { headers })
    );
  }

  //In get_access_data we are sending mail to the backend and validating the mail with already existing mails in blob ,if mail is their we are getting approval for access control
  get_access_data(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_access_data', (headers) =>
      this.http.post(`${this.url}/api/get_access_data/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // get_announcement_Inp_to_blob we are sending the instruction to backend and storing in blob
  get_announcement_Inp_to_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_announcement_Inp_to_blob', (headers) =>
      this.http.post(`${this.url}/api/get_announcement_Inp_to_blob/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // get_announcement_Inp_from_blob we are getting the announcement instructions from blob
  get_announcement_Inp_from_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_announcement_Inp_from_blob', (headers) =>
      this.http.post(`${this.url}/api/get_announcement_Inp_from_blob/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // get_Help_Instructions_to_blob we are sending the instruction to backend and storing in blob
  get_Help_Instructions_to_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_Help_Instructions_to_blob', (headers) =>
      this.http.post(`${this.url}/api/get_Help_Instructions_to_blob/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // get_Help_Instructions_from_blob we are getting the announcement instructions from blob
  get_Help_Instructions_from_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_Help_Instructions_from_blob', (headers) =>
      this.http.post(`${this.url}/api/get_Help_Instructions_from_blob/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // get_Feedback_form_to_blob we are sending the user feedbacks to backend and storing in blob and we have a post request to send the notification in teams
  get_Feedback_form_to_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_Feedback_form_to_blob', (headers) =>
      this.http.post(`${this.url}/api/get_Feedback_form_to_blob/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // uploadFile_to_blob we are sending the user files to backend and storing in blob
  uploadFile_to_blob(packages: File, Filename: string): Observable<any> {
    let formData = new FormData();
    formData.append('file', packages, Filename);

    // Do not set Content-Type header here; let the browser set multipart boundaries.
    return this.makeAuthenticatedRequest('uploadFile_to_blob', (headers) =>
      this.http.post(`${this.url}/api/uploadeFile_to_blob/`, formData, { headers, withCredentials: true })
    );
  }

  saveFavorites_to_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('saveFavorites_to_blob', (headers) =>
      this.http.post(`${this.url}/api/saveFavorites_to_blob/`, packages, { ...this.jsonConfig, headers })
    );
  }

  editFavorites_to_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('editFavorites_to_blob', (headers) =>
      this.http.post(`${this.url}/api/editFavorites_to_blob/`, packages, { ...this.jsonConfig, headers })
    );
  }

  delete_Favorites_in_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('delete_Favorites_in_blob', (headers) =>
      this.http.post(`${this.url}/api/delete_Favorites_in_blob/`, packages, { ...this.jsonConfig, headers })
    );
  }

  Favorites_form_blob(): Observable<any> {
    return this.makeAuthenticatedRequest('Favorites_form_blob', (headers) =>
      this.http.get(`${this.url}/api/Favorites_form_blob/`, { headers })
    );
  }

  sharedFavorites_form_blob(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('sharedFavorites_form_blob', (headers) =>
      this.http.post(`${this.url}/api/sharedFavorites_form_blob/`, packages, { ...this.jsonConfig, headers })
    );
  }

  //In SessionStore we are sending the seesion login data to backend
  SessionStore(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('SessionStore', (headers) =>
      this.http.post(`${this.url}/api/SessionStore/0/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  //In get_chart_details we getting the charts data
  get_chart_details(): Observable<any> {
    return this.makeAuthenticatedRequest('get_chart_details', (headers) =>
      this.http.get(`${this.url}/api/get_chart_details/`, { headers })
    );
  }

  //In get_Test_data we sending the Test inputs to backend and getting the local modification prediction, VM, PPD and Base oil results  to UI
  get_Test_data(packages: any): Observable<any> {
    return this.makeAuthenticatedRequest('get_Test_data', (headers) =>
      this.http.post(`${this.url}/api/get_Test_data/`, packages, { ...this.plainTextConfig, headers })
    );
  }

  // #endregion
}
